import { useEffect, useState } from "react";
import type { LogLine } from "../components/LogPanel";

interface HostLogsResponse {
  host_id: string;
  lines: LogLine[];
}

interface HostLogEvent {
  host_id: string;
  lines: LogLine[];
}

/**
 * Backfills the recent agent log buffer for a host, then subscribes to
 * `host:log` SSE events to append new lines as they arrive. Dedupes on
 * server-assigned `seq` so heartbeat redeliveries don't double-print.
 */
export function useHostLogs(hostId: string | null | undefined): {
  lines: LogLine[];
  live: boolean;
  error: string | null;
} {
  const [lines, setLines] = useState<LogLine[]>([]);
  const [live, setLive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initial backfill
  useEffect(() => {
    if (!hostId) return;
    let cancelled = false;
    setError(null);
    fetch(`/api/hosts/${hostId}/logs?limit=500`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as HostLogsResponse;
        if (!cancelled) setLines(data.lines ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [hostId]);

  // Live updates
  useEffect(() => {
    if (!hostId) return;
    const es = new EventSource("/api/events", { withCredentials: true });

    es.addEventListener("open", () => setLive(true));
    es.addEventListener("error", () => setLive(false));

    es.addEventListener("host:log", (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data) as HostLogEvent;
        if (!data || data.host_id !== hostId) return;
        if (!Array.isArray(data.lines) || data.lines.length === 0) return;
        setLines((prev) => {
          // Dedupe by seq number — agent batches can overlap on retry
          const seen = new Set(prev.map((l) => l.seq).filter((s): s is number => typeof s === "number"));
          const novel = data.lines.filter((l) => !(typeof l.seq === "number" && seen.has(l.seq)));
          if (novel.length === 0) return prev;
          // Cap memory: keep last 1000
          const merged = [...prev, ...novel];
          return merged.length > 1000 ? merged.slice(-1000) : merged;
        });
      } catch {
        // ignore malformed payload
      }
    });

    return () => {
      es.close();
      setLive(false);
    };
  }, [hostId]);

  return { lines, live, error };
}
